import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, ArrowRight, User, Lock, ShieldCheck, Leaf } from "lucide-react";
import { toast } from "sonner";

/**
 * KOVA Login — final approved reference design.
 *
 * Desktop composition mirrors the approved reference:
 *   LEFT  → matrix-agri hero (reference PNG as full-bleed background)
 *   RIGHT → dark translucent login panel with neon-green edge glow
 *
 * The real Three Zee AgroTech logo (`/kova/tzat-logo.jpg`) is rendered as an
 * overlay with CSS neon treatment (mix-blend-mode + drop-shadow) so the
 * ACTUAL company logo is what the user sees, not any AI redraw.
 *
 * Authentication logic (login(), forgot-password toast, remember-me localStorage)
 * is intentionally unchanged — this iteration is visual-only.
 */
export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem("kova_last_email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => Boolean(localStorage.getItem("kova_last_email")));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || "Sign in failed");
      return;
    }
    if (remember) localStorage.setItem("kova_last_email", email);
    else localStorage.removeItem("kova_last_email");
    toast.success("Welcome back");
  };

  const forgotPassword = () => {
    toast.info("Please contact your KOVA administrator to reset your password.", { duration: 4500 });
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0F0C] text-[#F8F9FA] relative overflow-hidden" data-testid="login-page">
      {/* --------------------------------------------------------------
          Layout
          - phone / tablet (< lg 1024px): stacked → compact hero banner
            on top, form panel below.
          - lg+ (≥1024px): 58 / 42 split — hero on the left, dark glass
            login panel on the right.
         -------------------------------------------------------------- */}
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[54%_46%] xl:grid-cols-[58%_42%]">
        {/* ============================================================ */}
        {/* LEFT / TOP — matrix-agri hero                                 */}
        {/*   • desktop: full-height column                               */}
        {/*   • tablet/phone: fixed-height banner at the top              */}
        {/* ============================================================ */}
        <div
          className="relative overflow-hidden h-[220px] sm:h-[260px] md:h-[300px] lg:h-auto bg-[position:50%_100%] lg:bg-[position:left_center]"
          style={{
            /* The cropped approved reference — matrix rain + TZAT/KOVA
               + farmland + tractor.  On desktop we anchor left (shows the
               full baked branding + landscape); on tablet/phone we shift
               to the BOTTOM (via Tailwind bg-[position:50%_100%] utility)
               so only the farmland strip is visible and the baked
               TZAT/KOVA branding is off-screen — avoids duplicating with
               the overlay rendered below. */
            backgroundImage: "url(/kova/login-hero-crop-v2.png)",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#0B0F0C",
          }}
        >
          {/* Soft right-edge fade (desktop only) → blend into the form
              panel.  Bottom fade on tablet/phone so the top hero merges
              gracefully into the form section below. */}
          <div
            className="hidden lg:block absolute inset-y-0 right-0 w-40 pointer-events-none"
            style={{
              background:
                "linear-gradient(to right, rgba(11,15,12,0) 0%, rgba(11,15,12,0.6) 55%, rgba(11,15,12,1) 100%)",
            }}
            aria-hidden
          />
          <div
            className="lg:hidden absolute inset-x-0 bottom-0 h-24 pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, rgba(11,15,12,0) 0%, rgba(11,15,12,0.75) 60%, rgba(11,15,12,1) 100%)",
            }}
            aria-hidden
          />

          {/* Tablet/phone-only compact TZAT · KOVA lockup pinned to the
              top-left of the banner — the desktop crop already has the
              approved lockup baked in, so we only render on <lg. Rendered
              inside the hero container but NOT aria-hidden so screen
              readers announce the brand. */}
          <div className="lg:hidden absolute top-4 left-4 right-4 flex items-center gap-3" role="banner">
            <div className="relative w-12 h-12 shrink-0 rounded-full bg-[#0B0F0C]/70 border border-[#39FF14]/50 p-0.5 shadow-[0_0_16px_rgba(57,255,20,0.4)] backdrop-blur">
              <img
                src="/kova/tzat-logo.jpg"
                alt="Three Zee AgroTech"
                className="w-full h-full rounded-full object-cover"
                style={{
                  mixBlendMode: "screen",
                  filter:
                    "brightness(1.35) contrast(1.15) saturate(1.6) drop-shadow(0 0 6px rgba(57,255,20,0.9))",
                }}
              />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.28em] text-[#B7BEC5] font-heading font-semibold truncate">
                Three Zee AgroTech
              </div>
              <div className="font-heading font-black text-[22px] leading-none tracking-[0.06em] text-[#F8F9FA] mt-1">
                TZAT{" "}
                <span className="text-[#39FF14] drop-shadow-[0_0_10px_rgba(57,255,20,0.6)]">
                  KOVA
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* RIGHT / BOTTOM — dark translucent glass login panel            */}
        {/* ============================================================ */}
        <div className="relative flex items-start lg:items-center justify-center px-5 py-8 sm:px-8 sm:py-10 lg:px-6 lg:py-10">
          <div className="relative w-full max-w-[420px] sm:max-w-[460px] lg:max-w-[540px] z-10">
            {/* Tablet/phone tagline (desktop shows it inside the hero image) */}
            <div className="lg:hidden mb-6">
              <p className="text-[13px] leading-relaxed text-[#B7BEC5]">
                Your intelligent companion in every{" "}
                <span className="text-[#39FF14] font-semibold">TZAT</span> operation.
              </p>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-[#8A8F98]">
                <Leaf size={12} className="text-[#39FF14]" />
                <span>Built in-house by Three Zee Agro Tech</span>
              </div>
            </div>

            {/* ---- Glass login card ---- */}
            <div
              className="relative rounded-[18px] border border-[#39FF14]/30 bg-[#0B0F0C]/80 backdrop-blur-xl p-6 sm:p-8 lg:p-8 xl:p-10 shadow-[0_0_40px_rgba(57,255,20,0.08),0_10px_60px_rgba(0,0,0,0.55)]"
              data-testid="login-card"
            >
              {/* Subtle inner top-edge glow */}
              <div
                aria-hidden
                className="absolute inset-x-6 top-0 h-px pointer-events-none"
                style={{ background: "linear-gradient(90deg, transparent, rgba(57,255,20,0.55), transparent)" }}
              />

              <h2
                className="font-heading font-bold text-[24px] sm:text-[28px] tracking-tight text-[#39FF14] drop-shadow-[0_0_12px_rgba(57,255,20,0.35)]"
                data-testid="login-welcome"
              >
                Welcome back
              </h2>
              <p className="text-[#B7BEC5] text-sm mt-1.5">Sign in to continue to KOVA</p>

              <form onSubmit={submit} className="mt-6 sm:mt-7 space-y-5" data-testid="login-form">
                {/* Email */}
                <div>
                  <Label
                    htmlFor="email"
                    className="text-[#B7BEC5] font-semibold text-[11px] uppercase tracking-[0.14em] font-heading"
                  >
                    Email or Username
                  </Label>
                  <div className="relative mt-2">
                    <User
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none"
                      aria-hidden
                    />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="pl-10 h-12 bg-[#121815]/80 border-[#39FF14]/15 text-[#F8F9FA] placeholder:text-[#8A8F98] rounded-[10px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#39FF14] focus-visible:!border-[#39FF14] focus-visible:!ring-0 transition-colors"
                      placeholder="Enter your email or username"
                      autoComplete="username"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <Label
                    htmlFor="password"
                    className="text-[#B7BEC5] font-semibold text-[11px] uppercase tracking-[0.14em] font-heading"
                  >
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none"
                      aria-hidden
                    />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="pl-10 h-12 bg-[#121815]/80 border-[#39FF14]/15 text-[#F8F9FA] placeholder:text-[#8A8F98] pr-11 rounded-[10px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#39FF14] focus-visible:!border-[#39FF14] focus-visible:!ring-0 transition-colors"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#8A8F98] hover:text-[#39FF14] rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#39FF14] focus-visible:outline-offset-1"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Row: remember-me + forgot */}
                <div className="flex items-center justify-between text-xs">
                  <label
                    className="inline-flex items-center gap-2 cursor-pointer group text-[#8A8F98] py-2 pr-2 -my-2"
                    data-testid="remember-me"
                  >
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 rounded border-[#3A4640] bg-[#121815] accent-[#39FF14] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#39FF14]"
                    />
                    <span className="font-medium group-hover:text-[#F8F9FA]">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={forgotPassword}
                    className="text-[#39FF14] font-semibold hover:text-[#F8F9FA] transition-colors py-2 pl-2 -my-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#39FF14] focus-visible:outline-offset-2 rounded"
                    data-testid="forgot-password-btn"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Error */}
                {error && (
                  <div
                    className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-[10px] px-3 py-2"
                    data-testid="login-error"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                {/* Sign in — outlined neon (matches approved reference) */}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="group w-full h-12 mt-2 bg-transparent border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#0B0F0C] font-heading font-bold tracking-wider text-sm rounded-[10px] transition-colors disabled:opacity-70 shadow-[0_0_20px_rgba(57,255,20,0.15)] hover:shadow-[0_0_30px_rgba(57,255,20,0.35)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#39FF14] focus-visible:!ring-0"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      Sign in
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  )}
                </Button>
              </form>

              {/* Footer inside the card */}
              <div className="mt-8 pt-5 border-t border-[#39FF14]/10 flex items-center justify-center gap-2 text-[11px] text-[#8A8F98]">
                <ShieldCheck size={12} className="text-[#39FF14]" />
                <span>
                  Secure. Trusted. Built for{" "}
                  <span className="text-[#39FF14] font-semibold">TZAT</span>.
                </span>
              </div>
            </div>

            {/* Micro-footer (desktop) */}
            <div className="hidden lg:flex justify-between items-center text-[10px] text-[#8A8F98] font-mono mt-6 px-1">
              <span>KOVA · v1.0</span>
              <span className="flex items-center gap-3">
                <a href="/privacy-policy" className="hover:text-[#39FF14] transition-colors" data-testid="login-privacy-link">Privacy Policy</a>
                <span>Built in-house by Three Zee Agro Tech</span>
              </span>
            </div>
            {/* Micro-footer (tablet/phone) */}
            <div className="lg:hidden text-center text-[10px] text-[#8A8F98] font-mono mt-6">
              KOVA · v1.0 · <a href="/privacy-policy" className="hover:text-[#39FF14] transition-colors" data-testid="login-privacy-link-mobile">Privacy Policy</a> · Built in-house by Three Zee Agro Tech
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
